import React from 'react';
import PropTypes from 'prop-types';
import { compose, graphql } from 'react-apollo';
import {
  PieChart as RPieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import {chooseColor, chooseScoreColor, getTotalAmount, setData, getUnit, toValue, toPercent, prepareParsedData} from "./utils";
import {
    LEAD_AGE_TOTALS_QUERY, LEAD_BROWSER_TOTALS_QUERY, LEAD_DEVICE_TYPE_TOTALS_QUERY,
    LEAD_DURATION_TOTALS_QUERY, LEAD_OS_TOTALS_QUERY, LEAD_AFFILIATE_NETWORK_TOTALS_QUERY,
    CONSUMER_ORIGIN_BY_PERIOD_QUERY, LEAD_SCORING_TOTAL, LEAD_RELATIVE_CLOSURE_BY_PERIOD
} from "./gql/query";
import {Loading} from "../../utils/loading";

const RADIAN = Math.PI / 180
const sortPie = (data = []) => {
  if(!data) return []
  return [...data].sort((a, b) => 
    b.leadsCount - a.leadsCount )
  }


const CustomLabel = (props) => {
  const {payload, unit, fill, stroke, fillOpacity, alignmentBaseline, textAnchor, 
    cx, cy, midAngle, innerRadius, outerRadius } = props
  const radius = innerRadius + (outerRadius - innerRadius) + 30
  const x = cx + radius * Math.cos((-midAngle - 1) * RADIAN);
  const y = cy + radius * Math.sin((-midAngle - 1) * RADIAN);
  return (
      <text
        x={x} fill={fill}
        y={y} stroke={stroke}
        fillOpacity={fillOpacity}
        alignmentBaseline={alignmentBaseline}
        textAnchor={textAnchor}
      >
      <tspan dy="0em">
          {(payload.leadsCount? payload.leadsCount: '<1') + unit}
        </tspan> 
      </text>
  )}


const CustomTooltip = props => {
  const { active } = props;
  if (active) {
    const { payload, totalAmount, data } = props;
    const amount = typeof totalAmount === "object"
      ? totalAmount.leadsCount
      : totalAmount
    const valueAbs = toValue(data, payload[0].payload.groupName, 'groupName')
    const percentValue = toPercent(data, payload[0].payload.groupName, amount, 'groupName')
    return (
      <div className="custom-tooltip">
        <div className="label" style={{textAlign: 'center'}}>
          <p>{payload[0].name}</p>
          <p>{`${valueAbs} (${percentValue})`}</p>
        </div>
      </div>
    );
  }

  return null;
}



class PieChart extends React.Component {
  static propTypes = {
    metric: PropTypes.string.isRequired,
    dateFrom: PropTypes.object.isRequired,
    dateTo: PropTypes.object.isRequired,
    groups: PropTypes.array,
    projects: PropTypes.array,
    labelType: PropTypes.string,
    labelValues: PropTypes.object,
    unit: PropTypes.string,
  }

  state = {
    activeTitle: null
  }

  _getOptionsByMetric(metric) {
    return { nameKey: "groupTitle", dataKey: "leadsCount" }
  }

  onMouseEnter = (o) => {
    this.setState({ activeTitle: o.value });
  }

  onMouseLeave = (o) => {
    this.setState({ activeTitle: null });
  }

  render() {
    const {metric, unit} = this.props
    const opts = this._getOptionsByMetric(metric);
    let data = this.props.data;
    if (!data || data.loading) {
      return <Loading/>;
    }
    data = data.data ? data.data : [];
    const totalAmount = getTotalAmount({data})
    const pieData = setData({data}, totalAmount, unit)
    return (
      <ResponsiveContainer>
        <RPieChart>
          <Pie
            isAnimationActive={false} label={<CustomLabel unit={getUnit(unit)}/>} 
            data={pieData} dataKey={opts.dataKey} minAngle={5}
            nameKey={opts.nameKey} fill='#bcbcbc' outerRadius={100} 
          >
            {data.map((item, i) => {
              const color = !~['score', 'lead-closure-relative'].indexOf(this.props.metric)
                ? chooseColor(i === 0)
                : chooseScoreColor(item.groupTitle)
              return <Cell
                key={i} fill={color} stroke={color}
                fillOpacity={this.state.activeTitle === item.groupTitle ? 0.7 : 1}/>
            })}
          </Pie>
          <Tooltip content={<CustomTooltip data={data} totalAmount={totalAmount} unit={getUnit(unit)}/>}/>
          <Legend
            onMouseEnter={this.onMouseEnter}
            onMouseLeave={this.onMouseLeave}
            layout='vertical'
            verticalAlign='middle'
            align='right'
            wrapperStyle={{ right: '-12px'}}
            iconType='circle'
            iconSize={12}
          />
        </RPieChart>
      </ResponsiveContainer>
    )
  }
}

const queryOptions = ({dateFrom, dateTo, projects, labelType, labelValues,
                        osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region }) => {
  return {
    variables: {
      dateFrom, dateTo, projects, labelType,
      labelValues, osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
    }, notifyOnNetworkStatusChange: true
  }
}

const groupsQueryOptions = ({dateFrom, dateTo, groups, projects, labelType, labelValues,
                        osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region  }) => {
  return {
    variables: {
      dateFrom, dateTo, groups, projects, labelType,
      labelValues, osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
    }, notifyOnNetworkStatusChange: true
  }
}

export default compose(
  graphql(LEAD_AGE_TOTALS_QUERY, {
    options: groupsQueryOptions,
    skip: (props) => (props.metric !== 'lead-age'),
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      const res = sortPie(data);
      return { ...ownProps, data: { loading, data: res, refetch }}
    }
  }),
  graphql(LEAD_DURATION_TOTALS_QUERY, {
    options: groupsQueryOptions,
    skip: (props) => (props.metric !== 'lead-duration'),
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      const res = sortPie(data);
      return { ...ownProps, data: { loading, data: res, refetch }}
    }
  }),
  graphql(LEAD_BROWSER_TOTALS_QUERY, {
    options: ({metric, dateFrom, dateTo, projects, labelType, labelValues,
                        osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region }) => {
      const isMobile = metric === 'lead-browsers-mobile';
      return {
        variables: {
          dateFrom, dateTo, isMobile, projects, labelType,
          labelValues, osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
        }, notifyOnNetworkStatusChange: true
      }
    },
    skip: (props) => (props.metric !== 'lead-browsers-mobile' && props.metric !== 'lead-browsers-desktop'),
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      const res = sortPie(data);
      return { ...ownProps, data: { loading, data: res, refetch }}
    }
  }),
  graphql(LEAD_DEVICE_TYPE_TOTALS_QUERY, {
    options: queryOptions,
    skip: (props) => (props.metric !== 'lead-device-types'),
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      const res = sortPie(data);
      return { ...ownProps, data: { loading, data: res, refetch }}
    }
  }),
  graphql(LEAD_OS_TOTALS_QUERY, {
    options: ({metric, dateFrom, dateTo, projects, labelType, labelValues,
                        osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region }) => {
      const isMobile = metric === 'lead-os-mobile';
      return {
        variables: {
          dateFrom, dateTo, isMobile, projects, labelType,
          labelValues, osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
        }, notifyOnNetworkStatusChange: true
      }
    },
    skip: (props) => (props.metric !== 'lead-os-mobile' && props.metric !== 'lead-os-desktop'),
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      const res = sortPie(data);
      return { ...ownProps, data: { loading, data: res, refetch }}
    }
  }),
  graphql(LEAD_AFFILIATE_NETWORK_TOTALS_QUERY, {
    options: queryOptions,
    skip: (props) => (props.metric !== 'lead-affiliate-network'),
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      const res = sortPie(data);
      return { ...ownProps, data: { loading, data: res, refetch }}
    }
  }),
  graphql(CONSUMER_ORIGIN_BY_PERIOD_QUERY, {
    options: ({
      metric, dateFrom, dateTo, groups, projects, labelType,
      labelValues, osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
    }) => {
      const bySubnet = metric === 'consumer-origin-by-ip';
      return {
        variables: {
          dateFrom, dateTo, groups, projects, bySubnet, labelType, labelValues,
          osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
        },
        notifyOnNetworkStatusChange: true
      };
    },
    skip: (props) => {
      return -1 === ['consumer-origin-by-ip', 'consumer-origin-by-device'].indexOf(props.metric);
    },
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      const res = sortPie(prepareParsedData(data).data);
      return { ...ownProps, data: { loading, data: res, refetch }}
    }
  }),
  graphql(LEAD_SCORING_TOTAL, {
    options: ({
      dateFrom, dateTo, groups, projects, labelType, scoringTypes = ["good", "low", "mid", "high"],
      labelValues, osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
    }) => {
      return {
        variables: {
          dateFrom, dateTo, groups, projects, labelType, labelValues, scoringTypes,
          osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
        },
        notifyOnNetworkStatusChange: true
      };
    },
    skip: (props) => (props.metric !== 'score'),
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      const res = sortPie(data);
      return { ...ownProps, data: { loading, data: res, refetch }}
    }
  }),
  graphql(LEAD_RELATIVE_CLOSURE_BY_PERIOD, {
    options: ({
      dateFrom, dateTo, groups, projects, labelType, scoringTypes = ["good", "low", "mid", "high"],
      labelValues, osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
    }) => {
      return {
        variables: {
          dateFrom, dateTo, groups, projects, labelType, labelValues, scoringTypes,
          osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
        },
        notifyOnNetworkStatusChange: true
      };
    },
    skip: (props) => (props.metric !== 'lead-closure-relative'),
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      const res = sortPie(prepareParsedData(data).data);
      return { ...ownProps, data: { loading, data: res, refetch }}
    }
  }),
)(PieChart);



// WEBPACK FOOTER //
// ./src/components/dashboard/analytics/pie-chart.js